import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { presupuestosApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Copy, 
  Trash2, 
  Send,
  FileText
} from 'lucide-react';

export default function PresupuestosPage() {
  const { empresaActual, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const loadPresupuestos = async () => {
    if (!empresaActual?.id) return;
    
    try {
      const estado = filtroEstado === 'todos' ? undefined : filtroEstado;
      const response = await presupuestosApi.getAll(empresaActual.id, estado);
      setPresupuestos(response.data);
    } catch (error) {
      console.error('Error loading presupuestos:', error);
      toast.error('Error al cargar los presupuestos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPresupuestos();
  }, [empresaActual?.id, filtroEstado]);

  const handleDuplicar = async (id) => {
    try {
      await presupuestosApi.duplicar(id);
      toast.success('Presupuesto duplicado');
      loadPresupuestos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este presupuesto?')) return;
    
    try {
      await presupuestosApi.delete(id);
      toast.success('Presupuesto eliminado');
      loadPresupuestos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEnviar = async (id) => {
    try {
      await presupuestosApi.enviar(id);
      toast.success('Presupuesto enviado correctamente');
      loadPresupuestos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
  };

  const filteredPresupuestos = presupuestos.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    return (
      p.numero.toLowerCase().includes(searchLower) ||
      p.cliente?.nombre?.toLowerCase().includes(searchLower) ||
      p.cliente?.empresa_cliente?.toLowerCase().includes(searchLower)
    );
  });

  const canEdit = hasPermission(['OWNER', 'MANAGER']);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn" data-testid="presupuestos-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuestos
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona todos tus presupuestos y cotizaciones
            </p>
          </div>
          {canEdit && (
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/presupuestos/nuevo')}
              data-testid="presupuestos-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Presupuesto
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por número, cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="presupuestos-search"
                />
              </div>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="w-full md:w-48" data-testid="presupuestos-filter-estado">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="aprobado">Aprobado</SelectItem>
                  <SelectItem value="rechazado">Rechazado</SelectItem>
                  <SelectItem value="vencido">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : filteredPresupuestos.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">No hay presupuestos</h3>
              <p className="empty-state-description">
                {searchTerm || filtroEstado !== 'todos' 
                  ? 'No se encontraron presupuestos con esos filtros'
                  : 'Crea tu primer presupuesto para empezar'}
              </p>
              {canEdit && !searchTerm && filtroEstado === 'todos' && (
                <Button 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/presupuestos/nuevo')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Presupuesto
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPresupuestos.map((p) => (
                  <TableRow 
                    key={p.id} 
                    className="table-row-hover cursor-pointer"
                    onClick={() => navigate(`/presupuestos/${p.id}`)}
                    data-testid={`presupuesto-row-${p.numero}`}
                  >
                    <TableCell className="font-medium">{p.numero}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{p.cliente?.nombre || '-'}</p>
                        <p className="text-sm text-slate-500">{p.cliente?.empresa_cliente || ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(p.created_at)}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(p.total)}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${getStatusColor(p.estado)}`}>
                        {getStatusLabel(p.estado)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/presupuestos/${p.id}`); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver detalle
                          </DropdownMenuItem>
                          {canEdit && p.estado === 'borrador' && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEnviar(p.id); }}>
                              <Send className="w-4 h-4 mr-2" />
                              Enviar
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicar(p.id); }}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicar
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={(e) => { e.stopPropagation(); handleEliminar(p.id); }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
